import { useEffect, useState, useRef } from "react";
import { NavLink, Route, Routes, useNavigate } from "react-router-dom";
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis,
    Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
    LayoutDashboard, Package, ShoppingBag, Store, Plus, Trash2, TrendingUp,
    Wine, Calendar, Edit2, AlertCircle, Truck, Settings, Gift, Upload, X, ImagePlus,
} from "lucide-react";
import { toast } from "sonner";
import VendorShipments from "./VendorShipments";
import VendorShippingConfig from "./VendorShippingConfig";
import VendorCatalog from "./VendorCatalog";
import VendorOrders from "./VendorOrders";
import VendorReferrals from "./VendorReferrals";

const WINE = "#7A2A36";
const GOLD = "#D4A93C";
const VINE = "#4F7942";

export default function VendorDashboard() {
    return (
        <>
            <section className="pt-10 pb-20 bg-white min-h-[80vh]">
                <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <Sidebar />
                    <div className="lg:col-span-10 space-y-8">
                        <TopBar />
                        <Routes>
                            <Route index element={<Overview />} />
                            <Route path="vinhos" element={<VendorCatalog kind="wine" />} />
                            <Route path="produtos" element={<VendorCatalog kind="product" />} />
                            <Route path="encomendas" element={<VendorOrders />} />
                            <Route path="envios" element={<VendorShipments />} />
                            <Route path="envios/config" element={<VendorShippingConfig />} />
                            <Route path="financeiro" element={<Financial />} />
                            <Route path="indicacoes" element={<VendorReferrals />} />
                            <Route path="perfil" element={<ProducerProfile />} />
                        </Routes>
                    </div>
                </div>
            </section>
        </>
    );
}

function TopBar() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    return (
        <header className="flex items-center justify-between bg-white rounded-md border border-gold-soft/30 px-6 py-4 shadow-sm">
            <div>
                <span className="label-overline text-wine">Painel do produtor</span>
                <h1 className="font-heading text-2xl text-wine-dark leading-none mt-1">
                    Bem-vindo, {user?.name?.split(" ")[0]}
                </h1>
            </div>
            <div className="flex items-center gap-4">
                <span className="text-xs text-wine-dark/60 hidden md:flex items-center gap-2">
                    <Calendar className="h-4 w-4" strokeWidth={1.5} />
                    {new Date().toLocaleDateString("pt-PT", { weekday: "long", day: "numeric", month: "long" })}
                </span>
                <button
                    onClick={async () => { await logout(); nav("/"); }}
                    className="btn-wine-outline text-xs px-5 py-2.5"
                >
                    Sair
                </button>
            </div>
        </header>
    );
}

function Sidebar() {
    const { user } = useAuth();
    const links = [
        { to: "/vendor", label: "Dashboard", icon: LayoutDashboard, end: true },
        { to: "/vendor/vinhos", label: "Vinhos", icon: Wine },
        { to: "/vendor/produtos", label: "Produtos", icon: Package },
        { to: "/vendor/encomendas", label: "Encomendas", icon: ShoppingBag },
        { to: "/vendor/envios", label: "Envios", icon: Truck },
        { to: "/vendor/envios/config", label: "Config. envio", icon: Settings },
        { to: "/vendor/financeiro", label: "Financeiro", icon: TrendingUp },
        { to: "/vendor/indicacoes", label: "Indicações", icon: Gift },
        { to: "/vendor/perfil", label: "Quinta", icon: Store },
    ];
    return (
        <aside className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-md border border-gold-soft/30 p-5 shadow-sm">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-vineyard rounded-md flex items-center justify-center text-cream font-heading text-xl">
                        {user?.name?.charAt(0) || "P"}
                    </div>
                    <div>
                        <span className="label-overline text-wine">Produtor</span>
                        <p className="font-heading text-base text-wine-dark leading-tight mt-0.5 truncate max-w-[140px]">
                            {user?.name}
                        </p>
                    </div>
                </div>
            </div>
            <nav className="bg-white rounded-md border border-gold-soft/30 p-2 shadow-sm">
                {links.map(({ to, label, icon: Icon, end }) => (
                    <NavLink
                        key={to}
                        to={to}
                        end={end}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md text-xs transition-all ${
                                isActive ? "bg-wine text-cream font-semibold" : "text-wine-dark hover:bg-wine/5"
                            }`
                        }
                        data-testid={`vendor-nav-${label}`}
                    >
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                        <span className="flex-1 uppercase tracking-wider">{label}</span>
                    </NavLink>
                ))}
            </nav>
        </aside>
    );
}

/* ---------- OVERVIEW ---------- */
function Overview() {
    const [stats, setStats] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [producer, setProducer] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/vendor/dashboard"),
            api.get("/vendor/analytics"),
            api.get("/producers/by-user/me").catch(() => ({ data: null })),
        ]).then(([s, a, p]) => { setStats(s.data); setAnalytics(a.data); setProducer(p.data); }).catch(() => {});
    }, []);

    if (!stats || !analytics) return <p className="text-wine-dark/60 label-overline">A carregar…</p>;

    return (
        <div className="space-y-8">
            {producer && !producer.approved && (
                <div className="bg-gold-soft/15 border border-gold-soft/60 rounded-md p-5 flex items-start gap-3" data-testid="vendor-approval-banner">
                    <AlertCircle className="h-5 w-5 text-gold-deep flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                        <p className="font-semibold text-wine-dark">A sua candidatura está em revisão</p>
                        <p className="text-sm text-wine-dark/70 mt-1">
                            Os seus produtos só ficarão visíveis publicamente após a aprovação pela equipa Caseirinho.
                        </p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard label="Receita líquida (30d)" value={`€${analytics.period.revenue_30d.toFixed(2)}`} sub={`Após ${analytics.period.commission_pct}% comissão`} icon={TrendingUp} highlight />
                <KpiCard label="Vendas totais" value={`€${stats.sales.toFixed(2)}`} sub="Acumulado" icon={Wine} />
                <KpiCard label="Encomendas (30d)" value={analytics.period.orders_30d} sub={`${stats.orders_count} totais`} icon={ShoppingBag} />
                <KpiCard label="A receber" value={`€${stats.pending_payouts.toFixed(2)}`} sub="Próximo payout" icon={TrendingUp} />
            </div>

            {/* Revenue chart */}
            <div className="bg-white rounded-md border border-gold-soft/30 p-6 shadow-sm">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <span className="label-overline text-wine/60">Receita líquida</span>
                        <h2 className="font-heading text-2xl text-wine-dark mt-1">Últimos 30 dias</h2>
                    </div>
                    <Legend color={WINE} label={`Após comissão ${analytics.period.commission_pct}%`} />
                </div>
                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={analytics.revenue_series}>
                            <defs>
                                <linearGradient id="vendorRev" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={WINE} stopOpacity={0.4} />
                                    <stop offset="100%" stopColor={WINE} stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#7A2A36" strokeOpacity={0.08} vertical={false} />
                            <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#7A2A36" strokeOpacity={0.4} fontSize={11} />
                            <YAxis stroke="#7A2A36" strokeOpacity={0.4} fontSize={11} tickFormatter={(v) => `€${v}`} />
                            <Tooltip
                                contentStyle={{ background: "#FBF8F3", border: "1px solid #D4A93C", borderRadius: 6, fontSize: 12 }}
                                formatter={(v) => [`€${Number(v).toFixed(2)}`, "Receita líquida"]}
                            />
                            <Area type="monotone" dataKey="revenue" stroke={WINE} strokeWidth={2} fill="url(#vendorRev)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Two-col: top products + recent orders */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-md border border-gold-soft/30 p-6 shadow-sm">
                    <span className="label-overline text-wine/60">Best-sellers</span>
                    <h2 className="font-heading text-2xl text-wine-dark mt-1 mb-5">Top produtos</h2>
                    {analytics.top_products.length === 0 ? (
                        <p className="text-wine-dark/60 text-sm">Sem vendas ainda.</p>
                    ) : (
                        <div className="space-y-3">
                            {analytics.top_products.map((p, i) => (
                                <div key={p.id} className="flex items-center gap-3 p-2 hover:bg-cream rounded-md transition-colors">
                                    <span className="font-heading text-2xl text-wine-dark/30 w-7">{i + 1}</span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-wine-dark truncate font-semibold">{p.name}</p>
                                        <p className="text-xs text-wine-dark/60">{p.units} un · €{p.revenue.toFixed(2)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-md border border-gold-soft/30 p-6 shadow-sm">
                    <span className="label-overline text-wine/60">Recentes</span>
                    <h2 className="font-heading text-2xl text-wine-dark mt-1 mb-5">Encomendas</h2>
                    {analytics.recent_orders.length === 0 ? (
                        <p className="text-wine-dark/60 text-sm">Sem encomendas ainda.</p>
                    ) : (
                        <div className="space-y-2">
                            {analytics.recent_orders.map((o) => (
                                <div key={o.id} className="flex items-center justify-between border-b border-wine-dark/5 last:border-0 py-2">
                                    <div>
                                        <p className="font-mono text-xs text-wine-dark">#{o.id.slice(0, 8)}</p>
                                        <p className="text-xs text-wine-dark/60">
                                            {new Date(o.created_at).toLocaleDateString("pt-PT")} · {o.items} itens
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-heading text-base text-wine-dark">€{o.total.toFixed(2)}</p>
                                        <span className="label-overline text-vineyard text-[9px]">Paga</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Units bar chart */}
            <div className="bg-white rounded-md border border-gold-soft/30 p-6 shadow-sm">
                <span className="label-overline text-wine/60">Unidades vendidas</span>
                <h2 className="font-heading text-2xl text-wine-dark mt-1 mb-4">Volume diário</h2>
                <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.revenue_series}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#7A2A36" strokeOpacity={0.08} vertical={false} />
                            <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} stroke="#7A2A36" strokeOpacity={0.4} fontSize={11} />
                            <YAxis stroke="#7A2A36" strokeOpacity={0.4} fontSize={11} allowDecimals={false} />
                            <Tooltip
                                contentStyle={{ background: "#FBF8F3", border: "1px solid #D4A93C", borderRadius: 6, fontSize: 12 }}
                                formatter={(v) => [v, "Unidades"]}
                            />
                            <Bar dataKey="units" fill={VINE} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

/* ---------- FINANCIAL ---------- */
function Financial() {
    const [stats, setStats] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    useEffect(() => {
        Promise.all([api.get("/vendor/dashboard"), api.get("/vendor/analytics")])
            .then(([s, a]) => { setStats(s.data); setAnalytics(a.data); }).catch(() => {});
    }, []);
    if (!stats || !analytics) return <p className="text-wine-dark/60 label-overline">A carregar…</p>;
    const grossRev = stats.sales;
    const commission = grossRev * (analytics.period.commission_pct / 100);
    return (
        <div>
            <Section title="Financeiro" subtitle="Receita, comissões e payouts">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <KpiCard label="Receita bruta" value={`€${grossRev.toFixed(2)}`} icon={TrendingUp} />
                    <KpiCard label="Comissão plataforma" value={`-€${commission.toFixed(2)}`} sub={`${analytics.period.commission_pct}%`} icon={Wine} />
                    <KpiCard label="Total a si" value={`€${(grossRev - commission).toFixed(2)}`} icon={TrendingUp} highlight />
                </div>

                <div className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm space-y-5">
                    <h3 className="label-overline text-wine">Como funciona o split</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-wine-dark/80">
                        <FlowStep n="1" title="Cliente paga" desc="Pagamento processado pela Stripe." />
                        <FlowStep n="2" title="Split automático" desc={`${analytics.period.commission_pct}% retidos pela plataforma.`} />
                        <FlowStep n="3" title="Reservado para si" desc="A diferença é registada como payout pendente." />
                        <FlowStep n="4" title="Transferência" desc="Recebe automaticamente após envio confirmado." />
                    </div>
                </div>

                <div className="mt-6 bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm">
                    <h3 className="label-overline text-wine mb-4">Pagamentos a receber</h3>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-wine-dark/70">Próximo payout</p>
                            <p className="font-heading text-4xl text-wine-dark mt-1">€{stats.pending_payouts.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-wine-dark/70">Acumulado total</p>
                            <p className="font-heading text-2xl text-wine-dark mt-1">€{stats.total_payouts.toFixed(2)}</p>
                        </div>
                    </div>
                </div>
            </Section>
        </div>
    );
}

function FlowStep({ n, title, desc }) {
    return (
        <div>
            <span className="label-overline text-gold-deep">{`Passo ${n}`}</span>
            <p className="font-heading text-lg text-wine-dark mt-2">{title}</p>
            <p className="text-xs text-wine-dark/65 mt-1 leading-relaxed">{desc}</p>
        </div>
    );
}

/* ---------- PRODUCTS ---------- */
function Products() {
    const [products, setProducts] = useState([]);
    const [show, setShow] = useState(false);
    const [editing, setEditing] = useState(null);
    const empty = { name: "", description: "", price: "", stock: "", category: "Vinho Tinto", region: "Douro", limited_edition: false, images: [""] };
    const [form, setForm] = useState(empty);

    async function load() {
        const { data } = await api.get("/vendor/products");
        setProducts(data);
    }
    useEffect(() => { load(); }, []);

    function openNew() {
        setEditing(null);
        setForm(empty);
        setShow(true);
    }

    function openEdit(p) {
        setEditing(p);
        setForm({
            name: p.name, description: p.description, price: p.price, stock: p.stock,
            category: p.category, region: p.region, limited_edition: p.limited_edition,
            images: p.images && p.images.length > 0 ? p.images : [""],
        });
        setShow(true);
    }

    async function submit(e) {
        e.preventDefault();
        const payload = {
            ...form,
            price: parseFloat(form.price),
            stock: parseInt(form.stock),
            images: form.images.filter(Boolean),
        };
        try {
            if (editing) {
                await api.patch(`/products/${editing.id}`, payload);
                toast.success("Produto atualizado");
            } else {
                await api.post("/products", payload);
                toast.success("Produto submetido — a aguardar aprovação");
            }
            setShow(false);
            load();
        } catch (e) {
            toast.error("Erro ao guardar produto");
        }
    }

    async function del(id) {
        if (!window.confirm("Eliminar produto?")) return;
        await api.delete(`/products/${id}`);
        load();
    }

    function setImg(i, v) {
        const arr = [...form.images];
        arr[i] = v;
        setForm({ ...form, images: arr });
    }
    function addImg() {
        setForm({ ...form, images: [...form.images, ""] });
    }
    function removeImg(i) {
        const arr = form.images.filter((_, idx) => idx !== i);
        setForm({ ...form, images: arr.length === 0 ? [""] : arr });
    }

    return (
        <Section title="Produtos" subtitle={`${products.length} no catálogo`}>
            <div className="flex justify-end mb-4">
                <button onClick={openNew} className="btn-wine inline-flex items-center gap-2" data-testid="vendor-new-product">
                    <Plus className="h-4 w-4" /> Novo produto
                </button>
            </div>

            {show && (
                <div className="bg-white border border-gold-soft/30 rounded-md p-6 mb-6 shadow-sm">
                    <h3 className="font-heading text-2xl text-wine-dark mb-5">
                        {editing ? "Editar produto" : "Adicionar produto"}
                    </h3>
                    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="vendor-product-form">
                        <Field label="Nome" value={form.name} set={(v) => setForm({ ...form, name: v })} />
                        <Field label="Preço (€)" value={form.price} set={(v) => setForm({ ...form, price: v })} type="number" />
                        <Field label="Stock" value={form.stock} set={(v) => setForm({ ...form, stock: v })} type="number" />
                        <SelectField
                            label="Categoria"
                            value={form.category}
                            set={(v) => setForm({ ...form, category: v })}
                            options={["Vinho Tinto", "Vinho Branco", "Vinho do Porto", "Espumante", "Azeite", "Queijos", "Enchidos", "Compotas", "Chocolates"]}
                        />
                        <SelectField
                            label="Região"
                            value={form.region}
                            set={(v) => setForm({ ...form, region: v })}
                            options={["Douro", "Alentejo", "Dão", "Vinho Verde", "Beira Alta", "Bairrada", "Lisboa", "Madeira", "Açores"]}
                        />
                        <label className="flex items-center gap-2 mt-7">
                            <input type="checkbox" checked={form.limited_edition} onChange={(e) => setForm({ ...form, limited_edition: e.target.checked })} className="accent-wine" />
                            <span className="text-sm">Edição limitada</span>
                        </label>
                        <label className="block md:col-span-2">
                            <span className="label-overline text-wine-dark">Descrição</span>
                            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} required className="mt-2 w-full bg-transparent border border-wine-dark/20 rounded-md px-4 py-3 text-sm focus:border-wine" />
                        </label>
                        <div className="md:col-span-2">
                            <span className="label-overline text-wine-dark">Imagens (URLs)</span>
                            <div className="mt-2 space-y-2">
                                {form.images.map((url, i) => (
                                    <div key={i} className="flex gap-2">
                                        <input value={url} onChange={(e) => setImg(i, e.target.value)} placeholder="https://..." className="flex-1 bg-transparent border border-wine-dark/20 rounded-md px-3 py-2 text-sm focus:border-wine" data-testid={`vendor-image-${i}`} />
                                        {form.images.length > 1 && (
                                            <button type="button" onClick={() => removeImg(i)} className="text-destructive">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button type="button" onClick={addImg} className="label-overline text-wine hover:text-gold-deep flex items-center gap-1">
                                    <Plus className="h-3 w-3" /> Adicionar imagem
                                </button>
                            </div>
                        </div>
                        <div className="md:col-span-2 flex gap-3 justify-end">
                            <button type="button" onClick={() => setShow(false)} className="btn-wine-outline">Cancelar</button>
                            <button className="btn-wine" data-testid="vendor-product-submit">
                                {editing ? "Guardar alterações" : "Submeter"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {products.map((p) => (
                    <div key={p.id} className="bg-white border border-gold-soft/30 rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                        <div className="aspect-video bg-cream-warm overflow-hidden">
                            {p.images?.[0] ? (
                                <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-wine-dark/30">
                                    <Package className="h-12 w-12" strokeWidth={1} />
                                </div>
                            )}
                        </div>
                        <div className="p-5">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <h4 className="font-heading text-lg text-wine-dark leading-tight">{p.name}</h4>
                                <span className={`label-overline text-[9px] px-2 py-1 rounded ${p.approved ? "text-vineyard bg-vineyard/10" : "text-gold-deep bg-gold-soft/20"}`}>
                                    {p.approved ? "Aprovado" : "Pendente"}
                                </span>
                            </div>
                            <p className="text-sm text-wine-dark/60">€{p.price.toFixed(2)} · {p.stock} em stock</p>
                            <div className="mt-4 flex gap-2">
                                <button onClick={() => openEdit(p)} className="flex-1 label-overline text-xs px-3 py-2 border border-wine-dark/20 rounded hover:border-wine flex items-center justify-center gap-1">
                                    <Edit2 className="h-3 w-3" /> Editar
                                </button>
                                <button onClick={() => del(p.id)} className="px-3 py-2 text-destructive hover:bg-destructive/10 rounded">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {products.length === 0 && (
                    <div className="col-span-full bg-white border border-gold-soft/30 rounded-md p-12 text-center">
                        <Package className="h-10 w-10 text-wine/30 mx-auto" strokeWidth={1.5} />
                        <p className="mt-4 text-wine-dark/60">Ainda sem produtos. Adicione o primeiro.</p>
                    </div>
                )}
            </div>
        </Section>
    );
}

/* ---------- PROFILE ---------- */
function ProducerProfile() {
    const [producer, setProducer] = useState(null);
    const [form, setForm] = useState({});
    useEffect(() => {
        api.get("/producers/by-user/me").then((r) => {
            setProducer(r.data);
            const gallery = Array.isArray(r.data.gallery) && r.data.gallery.length
                ? r.data.gallery
                : (r.data.cover_image ? [r.data.cover_image] : []);
            setForm({ ...r.data, gallery });
        }).catch(() => {});
    }, []);
    async function save(e) {
        e.preventDefault();
        try {
            const gallery = form.gallery || [];
            const { data } = await api.patch("/producers/by-user/me", {
                producer_name: form.producer_name,
                region: form.region,
                story: form.story,
                gallery,
                cover_image: gallery[0] || "",
            });
            const g = Array.isArray(data.gallery) && data.gallery.length ? data.gallery : (data.cover_image ? [data.cover_image] : []);
            setProducer(data);
            setForm({ ...data, gallery: g });
            toast.success("Perfil atualizado");
        } catch { toast.error("Erro ao guardar"); }
    }
    if (!producer) return <p className="text-wine-dark/60 label-overline">A carregar…</p>;
    return (
        <Section title="A minha quinta" subtitle="Esta é a história que aparece publicamente.">
            {!producer.approved && (
                <div className="mb-6 bg-gold-soft/15 border border-gold-soft/60 p-4 rounded-md text-sm text-wine-dark" data-testid="approval-banner">
                    A sua candidatura está em revisão pela equipa Caseirinho.
                </div>
            )}
            <form onSubmit={save} className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm space-y-5 max-w-2xl">
                <Field label="Nome do produtor" value={form.producer_name || ""} set={(v) => setForm({ ...form, producer_name: v })} />
                <Field label="Região" value={form.region || ""} set={(v) => setForm({ ...form, region: v })} />
                <div>
                    <span className="label-overline text-wine-dark">Fotos da quinta (até 8)</span>
                    <p className="text-[11px] text-wine-dark/50 mt-1 mb-2">A 1ª foto é usada como imagem de capa pública.</p>
                    <QuintaGallery images={form.gallery || []} onChange={(g) => setForm({ ...form, gallery: g })} max={8} />
                </div>
                <label className="block">
                    <span className="label-overline text-wine-dark">A nossa história</span>
                    <textarea value={form.story || ""} onChange={(e) => setForm({ ...form, story: e.target.value })} rows={6} className="mt-2 w-full bg-transparent border border-wine-dark/20 rounded-md px-4 py-3 text-sm focus:border-wine" />
                </label>
                <button className="btn-wine" data-testid="save-producer">Guardar</button>
            </form>
        </Section>
    );
}

/* Galeria da quinta: miniaturas + upload (base64, Kubernetes-safe). Sem mostrar URLs. */
function QuintaGallery({ images, onChange, max = 8 }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const fullUrl = (url) => (url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

    async function handleFiles(files) {
        const arr = Array.from(files).slice(0, max - images.length);
        if (arr.length === 0) { toast.error(`Máximo de ${max} fotos`); return; }
        setUploading(true);
        const next = [...images];
        for (const file of arr) {
            if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: maior que 5MB`); continue; }
            try {
                const data_base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result.split(",")[1]);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
                const { data } = await api.post("/vendor/upload-image", {
                    filename: file.name, mime_type: file.type, data_base64,
                });
                next.push(data.url);
            } catch (e) {
                toast.error(`${file.name}: ${e.response?.data?.detail || "falha no upload"}`);
            }
        }
        onChange(next);
        setUploading(false);
        if (inputRef.current) inputRef.current.value = "";
    }
    function move(from, to) {
        if (to < 0 || to >= images.length) return;
        const next = [...images];
        const [it] = next.splice(from, 1);
        next.splice(to, 0, it);
        onChange(next);
    }
    return (
        <div data-testid="quinta-gallery">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {images.map((url, i) => (
                    <div key={i} className="relative group aspect-square" data-testid={`quinta-photo-${i}`}>
                        <img src={fullUrl(url)} alt="" className="w-full h-full object-cover rounded-md border border-wine-dark/10" />
                        {i === 0 && <span className="absolute top-1 left-1 bg-wine text-cream text-[9px] label-overline px-1.5 py-0.5 rounded">Capa</span>}
                        <div className="absolute inset-0 bg-elegant/0 group-hover:bg-elegant/60 transition-colors rounded-md flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                            {i > 0 && <button type="button" onClick={() => move(i, i - 1)} className="bg-cream text-wine p-1.5 rounded text-xs" data-testid={`quinta-photo-left-${i}`}>←</button>}
                            <button type="button" onClick={() => onChange(images.filter((_, idx) => idx !== i))} className="bg-destructive text-cream p-1.5 rounded" data-testid={`quinta-photo-remove-${i}`}><X className="h-3 w-3" /></button>
                            {i < images.length - 1 && <button type="button" onClick={() => move(i, i + 1)} className="bg-cream text-wine p-1.5 rounded text-xs" data-testid={`quinta-photo-right-${i}`}>→</button>}
                        </div>
                    </div>
                ))}
                {images.length < max && (
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="aspect-square border-2 border-dashed border-wine-dark/20 hover:border-wine/50 rounded-md flex flex-col items-center justify-center text-wine/60 hover:text-wine transition-colors bg-cream/50"
                        data-testid="quinta-upload-btn"
                    >
                        <ImagePlus className="h-6 w-6" strokeWidth={1.5} />
                        <span className="text-[11px] mt-1">{uploading ? "A enviar…" : "Adicionar foto"}</span>
                    </button>
                )}
            </div>
            <p className="text-[11px] text-wine-dark/45 mt-2">JPG, PNG ou WebP · até 5MB cada · {images.length}/{max} fotos</p>
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="quinta-photo-input"
            />
        </div>
    );
}

/* ---------- SHARED ---------- */
function KpiCard({ label, value, sub, highlight, icon: Icon }) {
    return (
        <div className={`p-6 rounded-md border shadow-sm ${highlight ? "bg-wine-dark text-cream border-wine-dark" : "bg-white border-gold-soft/30"}`}>
            <div className="flex items-start justify-between">
                <span className={`label-overline ${highlight ? "text-gold-soft" : "text-wine/60"}`}>{label}</span>
                {Icon && <Icon className={`h-4 w-4 ${highlight ? "text-gold-soft" : "text-wine/40"}`} strokeWidth={1.5} />}
            </div>
            <p className="font-heading text-3xl md:text-4xl mt-3 leading-none">{value}</p>
            {sub && <span className={`text-xs mt-3 inline-block ${highlight ? "text-cream/60" : "text-wine-dark/55"}`}>{sub}</span>}
        </div>
    );
}

function Legend({ color, label }) {
    return (
        <span className="flex items-center gap-2 text-xs text-wine-dark/60">
            <span className="w-3 h-3 rounded-sm" style={{ background: color }} />{label}
        </span>
    );
}

function Section({ title, subtitle, children }) {
    return (
        <div>
            <div className="mb-6">
                <h2 className="font-heading text-4xl text-wine-dark">{title}</h2>
                {subtitle && <p className="text-sm text-wine-dark/60 mt-2">{subtitle}</p>}
            </div>
            {children}
        </div>
    );
}

function Field({ label, value, set, type = "text", required = true }) {
    return (
        <label className="block">
            <span className="label-overline text-wine-dark">{label}</span>
            <input type={type} value={value} onChange={(e) => set(e.target.value)} required={required} className="mt-2 w-full bg-transparent border border-wine-dark/20 rounded-md px-4 py-3 text-sm focus:border-wine" />
        </label>
    );
}

function SelectField({ label, value, set, options }) {
    return (
        <label className="block">
            <span className="label-overline text-wine-dark">{label}</span>
            <select value={value} onChange={(e) => set(e.target.value)} className="mt-2 w-full bg-white border border-wine-dark/20 rounded-md px-4 py-3 text-sm focus:border-wine">
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </label>
    );
}
